import Modal from './Modal';
import { formatDateTime } from '../utils/dateFormat';
import type { TrackingInfo } from '../types';

interface TrackingDetailsModalProps {
  isOpen: boolean;
  loading: boolean;
  tracking: TrackingInfo | null;
  onClose: () => void;
}

function safeExternalTrackingUrl(value?: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? url.toString() : null;
  } catch {
    return null;
  }
}

function TrackingDetailsModal({ isOpen, loading, tracking, onClose }: TrackingDetailsModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Tracking Details">
      {loading ? (
        <div style={{ display: 'grid', gap: '12px' }}>
          <div className="skeleton" style={{ height: '40px' }} />
          <div className="skeleton" style={{ height: '100px' }} />
        </div>
      ) : tracking ? (
        <div>
          <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
            <div>
              <strong>Tracking Number:</strong>
              <div style={{ fontFamily: 'monospace', marginTop: '4px' }}>{tracking.tracking_code}</div>
            </div>
            <div>
              <strong>Carrier:</strong> {tracking.carrier}
            </div>
            <div>
              <strong>Status:</strong>{' '}
              <span className={`status-badge ${tracking.status_tone || 'info'}`}>{tracking.status_label}</span>
            </div>
            {tracking.est_delivery_date && (
              <div>
                <strong>Estimated Delivery:</strong>{' '}
                {new Date(tracking.est_delivery_date).toLocaleDateString()}
              </div>
            )}
          </div>

          {tracking.tracking_details && tracking.tracking_details.length > 0 && (
            <div>
              <strong style={{ display: 'block', marginBottom: '12px' }}>Tracking History:</strong>
              <div
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  background: 'var(--champagne-dune)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '12px',
                }}
              >
                {tracking.tracking_details.map((detail, i) => {
                  const structuredLocation = detail.tracking_location
                    ? [
                        detail.tracking_location.city,
                        detail.tracking_location.state,
                        detail.tracking_location.zip,
                        detail.tracking_location.country,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    : '';
                  const location = detail.location || structuredLocation;

                  return (
                    <div
                      key={`${detail.datetime}-${i}`}
                      style={{
                        padding: '12px 0',
                        borderBottom:
                          i < tracking!.tracking_details.length - 1
                            ? '1px solid rgba(201, 168, 129, 0.3)'
                            : 'none',
                      }}
                    >
                      <div style={{ fontSize: '0.85em', color: 'var(--aged-mahogany-light)' }}>
                        {formatDateTime(detail.datetime)}
                      </div>
                      <div style={{ fontWeight: 500, marginTop: '4px' }}>{detail.message}</div>
                      {location && (
                        <div style={{ fontSize: '0.85em', color: 'var(--aged-mahogany-light)', marginTop: '2px' }}>
                          {location}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {safeExternalTrackingUrl(tracking.public_url || tracking.tracking_url) && (
            <div style={{ marginTop: '20px' }}>
              <a
                href={safeExternalTrackingUrl(tracking.public_url || tracking.tracking_url) || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                View on Carrier Website
              </a>
            </div>
          )}
        </div>
      ) : (
        <p>No tracking information available.</p>
      )}
    </Modal>
  );
}

export default TrackingDetailsModal;
